import { Skeleton, cn } from "@nexus/ui";
import { Fragment } from "react";

import {
  ORDERS_CELL_SCRIPTS_CLASS,
  ORDERS_CELL_STATUS_CLASS,
  ORDERS_CELL_SENT_CLASS,
  ORDERS_CELL_DUE_CLASS,
  ORDERS_CELL_TYPE_CLASS,
  ORDERS_GRID_CLASS,
  ORDERS_ROW_PADDING_CLASS,
} from "./orders-list-grid";

const SKELETON_ROWS = 4;

/** Mirrors the toolbar + row grid so the page does not jump when data lands. */
export function OrdersListSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy aria-live="polite">
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton className="h-8 w-64 rounded-sm" />
        <Skeleton className="h-8 w-20 rounded-sm" />
        <Skeleton className="h-8 w-32 rounded-sm" />
        <Skeleton className="ml-auto h-8 w-24 rounded-sm" />
      </div>
      <div className="overflow-hidden rounded-md border border-border">
        <div className={cn(ORDERS_GRID_CLASS, ORDERS_ROW_PADDING_CLASS, "h-8 border-b border-border bg-muted/30")}>
          <span />
          <span />
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className={cn(ORDERS_CELL_TYPE_CLASS, "h-2.5 w-8")} />
          <Skeleton className={cn(ORDERS_CELL_SCRIPTS_CLASS, "h-2.5 w-10")} />
          <Skeleton className={cn(ORDERS_CELL_STATUS_CLASS, "h-2.5 w-12")} />
          <Skeleton className={cn(ORDERS_CELL_SENT_CLASS, "h-2.5 w-8")} />
          <Skeleton className={cn(ORDERS_CELL_DUE_CLASS, "h-2.5 w-8")} />
          <span />
        </div>
        <ul className="divide-y divide-border-subtle">
          {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <Fragment key={index}>
              <li className={cn(ORDERS_GRID_CLASS, ORDERS_ROW_PADDING_CLASS, "min-h-13 py-1.5")}>
                <Skeleton className="size-4 rounded-[4px]" />
                <Skeleton className="size-4 rounded-sm" />
                <span className="flex items-center gap-2.5">
                  <Skeleton className="size-7 rounded-md" />
                  <span className="flex min-w-0 flex-col gap-1">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-2.5 w-44" />
                  </span>
                </span>
                <Skeleton className={cn(ORDERS_CELL_TYPE_CLASS, "h-5 w-12 rounded-sm")} />
                <Skeleton className={cn(ORDERS_CELL_SCRIPTS_CLASS, "h-3 w-12")} />
                <span className={cn(ORDERS_CELL_STATUS_CLASS, "flex flex-col gap-1")}>
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-1 w-24 rounded-full" />
                </span>
                <Skeleton className={cn(ORDERS_CELL_SENT_CLASS, "h-3 w-10")} />
                <Skeleton className={cn(ORDERS_CELL_DUE_CLASS, "h-4 w-24 rounded-sm")} />
                <span />
              </li>
              {index === 1
                ? Array.from({ length: 2 }).map((__, childIndex) => (
                    <li
                      key={childIndex}
                      className={cn(
                        ORDERS_GRID_CLASS,
                        ORDERS_ROW_PADDING_CLASS,
                        "relative min-h-12 bg-muted/20 py-1.5",
                      )}
                    >
                      <span aria-hidden className="absolute bottom-0 left-[3.85rem] top-0 w-px bg-border" />
                      <span aria-hidden className="absolute left-[3.85rem] top-1/2 h-px w-5 bg-border" />
                      <Skeleton className="size-4 rounded-[4px]" />
                      <span />
                      <span className="flex min-w-0 flex-col gap-1 pl-8">
                        <Skeleton className="h-3 w-4/5" />
                        <Skeleton className="h-2.5 w-3/5" />
                      </span>
                      <Skeleton className={cn(ORDERS_CELL_TYPE_CLASS, "h-5 w-12 rounded-sm")} />
                      <span className={ORDERS_CELL_SCRIPTS_CLASS}>—</span>
                      <span className={cn(ORDERS_CELL_STATUS_CLASS, "text-muted-foreground/45")}>—</span>
                      <span className={cn(ORDERS_CELL_SENT_CLASS, "text-muted-foreground/45")}>—</span>
                      <Skeleton className={cn(ORDERS_CELL_DUE_CLASS, "h-3 w-20")} />
                      <span />
                    </li>
                  ))
                : null}
            </Fragment>
          ))}
        </ul>
      </div>
    </div>
  );
}
